import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

/**
 * Infinite-scroll sentinel hook.
 *
 * Returns a ref to attach to an invisible marker element
 * (`<div ref={sentinelRef} />`) placed at the bottom of a scrollable list.
 * When that marker enters the viewport of `rootRef`'s scroll container,
 * calls `onReachEnd` once and stops observing the sentinel until the effect
 * re-runs (e.g. a new page was appended, moving the sentinel — and the
 * observer watching it — further down the list).
 *
 * `onReachEnd` fires directly from the IntersectionObserver's own callback,
 * not from a separate `useEffect` reacting to an `isIntersecting` state
 * value — a state-driven effect would double-fire under React's
 * StrictMode double-invoke-effects behavior in development.
 * Pattern: docs/design-patterns/anti-patterns/useEffect-mutation-trigger.md
 *
 * @param rootRef - Ref to the scrollable container the IntersectionObserver
 *   measures intersection against (its `root` option). `root: null` (the
 *   ref not yet attached to an element) falls back to the browser viewport
 *   per the IntersectionObserver spec, rather than erroring.
 * @param onReachEnd - Called once when the sentinel becomes visible while
 *   `enabled` is true — typically `fetchNextPage`.
 * @param enabled - Gates whether an intersection calls `onReachEnd`. Pass
 *   `hasNextPage && !isFetchingNextPage` so an exhausted or already-loading
 *   lane doesn't refire.
 * @returns A ref to attach to the sentinel element.
 */
export function useLoadMoreSentinel(
  rootRef: RefObject<HTMLElement | null>,
  onReachEnd: () => void,
  enabled: boolean,
): RefObject<HTMLDivElement | null> {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && enabled) {
          onReachEnd();
          // Stop observing this sentinel immediately so a slow onReachEnd
          // (e.g. still awaiting the new page) can't fire a second time
          // while the element remains visible.
          observer.unobserve(sentinel);
        }
      },
      { root: rootRef.current, threshold: 0 },
    );

    observer.observe(sentinel);

    return (): void => {
      observer.disconnect();
    };
  }, [rootRef, onReachEnd, enabled]);

  return sentinelRef;
}
